import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { useState, useEffect, useMemo } from "react";
import CategoryManagerService from "../../services/dashboard/CategoryManager"; // adjust path as needed
import toast from "react-hot-toast";
import config from "../../config";
import { handleApiResponse } from "../../utils/apiResponseHandler"; // adjust path as needed
import { motion } from "framer-motion";
import AsyncCreatableSelect from "react-select/async-creatable";

export default function CategoryManager() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({ catName: "", description: "" });
  // The iconFile state can be either a File object (for a new upload) or a string URL (for editing)
  const [iconFile, setIconFile] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await CategoryManagerService.getAllCategories();
      const categoriesList = handleApiResponse(data) || [];
      setCategories(categoriesList);
    } catch (error) {
      toast.error("Failed to load categories.");
      console.error(error);
    }
  };

  const loadMatchingCategories = (inputValue) => {
    if (!inputValue.trim()) return [];

    return categories
      .filter((cat) =>
        cat.catName.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map((cat) => ({
        label: cat.catName,
        value: cat.catId,
        catData: cat,
      }));
  };

  const handleCategorySelect = (selectedOption) => {
    if (!selectedOption) return;

    if (selectedOption.catData) {
      handleEdit(selectedOption.catData); // edit if exists
    } else {
      setFormData({ ...formData, catName: selectedOption.label }); // create if new
    }
  };

  // ─── Handle create or update ───
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let response;

      if (editingCategory) {
        // Update existing category
        response = await CategoryManagerService.updateCategory(
          editingCategory.catId,
          formData,
          iconFile instanceof File ? iconFile : null
        );
      } else {
        // Create new category
        response = await CategoryManagerService.createCategory(
          formData,
          iconFile
        );
      }

      // Directly check responseCode & errorCode:
      if (response.responseCode === 200 && response.errorCode === null) {
        toast.success(
          editingCategory
            ? "Category updated successfully!"
            : "Category created successfully!"
        );
        // Reset form and modal state
        setFormData({ catName: "", description: "" });
        setIconFile(null);
        setEditingCategory(null);
        setShowModal(false);
        // Refresh list
        loadCategories();
      } else {
        // If the server returned an error message, show it; otherwise a generic one
        toast.error(response.responseMessage || "Failed to save category.");
      }
    } catch (error) {
      toast.error("Server error while saving category.");
      console.error(error);
    }
  };

  // ─── Handle delete ───
  const handleDelete = async (id) => {
    try {
      const response = await CategoryManagerService.deleteCategory(id);

      // Check response fields:
      if (response.responseCode === 200 && response.errorCode === null) {
        toast.success("Category deleted successfully!");
        loadCategories();
      } else {
        toast.error(response.responseMessage || "Failed to delete category.");
      }
    } catch (error) {
      toast.error("Server error while deleting category.");
      console.error(error);
    }
  };

  // ─── Populate form for edit ───
  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      catName: category.catName,
      description: category.description,
    });
    if (category.iconUrl) {
      setIconFile(`${config.url}/categoryIcons/${category.iconUrl}`);
    } else {
      setIconFile(null);
    }
    setShowModal(true);
  };

  // Filter categories by search term
  const filteredCategories = useMemo(
    () =>
      categories.filter((cat) =>
        cat.catName.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [categories, searchTerm]
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1600px] mx-auto p-6">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          ← Back
        </button>
        
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Category Manager
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Organize and manage book categories
            </p>
          </div>

          {/* Add Category Button */}
          <button
            onClick={() => {
              setEditingCategory(null);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>

      {/* Category Form Modal */}
      <Modal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCategory(null);
          setFormData({ catName: "", description: "" });
          setIconFile(null);
        }}
        title={editingCategory ? "Edit Category" : "Add Category"}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Category Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
              Category Name
            </label>
            {editingCategory ? (
              <input
                type="text"
                value={formData.catName}
                onChange={(e) =>
                  setFormData({ ...formData, catName: e.target.value })
                }
                placeholder="Category name"
                className="w-full rounded-md border border-gray-300 p-2 dark:bg-gray-800 dark:text-white"
              />
            ) : (
              <AsyncCreatableSelect
                isClearable
                cacheOptions
                defaultOptions={categories.map((cat) => ({
                  label: cat.catName,
                  value: cat.catId,
                  catData: cat,
                }))}
                loadOptions={(inputValue) =>
                  Promise.resolve(loadMatchingCategories(inputValue))
                }
                onChange={handleCategorySelect}
                placeholder="Search or enter new category..."
                value={
                  formData.catName
                    ? { label: formData.catName, value: formData.catName }
                    : null
                }
                onCreateOption={(inputValue) => {
                  setFormData({ ...formData, catName: inputValue });
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    padding: "2px",
                    borderRadius: "0.5rem",
                    borderColor: "#ccc",
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Enter description"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF7236] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows="4"
            ></textarea>
          </div>

          {/* Icon Upload with Preview */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Icon (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // When a new file is selected, update the iconFile state.
                  setIconFile(file);
                }
              }}
              className="block  w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border file:rounded-lg file:text-sm file:font-semibold file:bg-[#FF7236]/10 file:text-[#FF7236] hover:file:bg-[#FF7236]/20"
            />

            {/* Icon Preview */}
            {iconFile && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                  Preview:
                </p>
                <img
                  // If iconFile is a file, create a temporary URL, otherwise use the string URL directly.
                  src={
                    typeof iconFile === "string"
                      ? iconFile
                      : URL.createObjectURL(iconFile)
                  }
                  alt="Icon Preview"
                  className="w-20 h-20 object-cover border rounded-md shadow-sm"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end pt-2">
            <button
              type="submit"
              className="bg-[#FF7236] text-white px-5 py-2 rounded-md hover:bg-[#D74A0E] transition"
            >
              {editingCategory ? "Update Category" : "Create Category"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingCategory(null);
                setFormData({ catName: "", description: "" });
                setIconFile(null);
              }}
              className="bg-gray-200 text-black px-5 py-2 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

        {/* Elite Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search categories by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
            />
          </div>
        </div>

        {/* Elite Category Grid - Seamless Integration */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9 gap-2">
          {filteredCategories.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                No categories found.
              </p>
            </div>
          ) : (
            filteredCategories.map((cat) => (
              <motion.div
                key={cat.catId}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="group bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ease-out overflow-hidden cursor-pointer"
              >
                {/* Card Content - Perfectly Aligned */}
                <div className="p-3.5 flex flex-col h-full min-h-[110px]">
                  {/* Icon - Top Section */}
                  {cat.iconUrl && (
                    <div className="w-7 h-7 mb-3 flex-shrink-0">
                      <img
                        src={`${config.url}/categoryIcons/${cat.iconUrl}`}
                        alt={cat.catName}
                        className="w-full h-full object-contain opacity-40 group-hover:opacity-70 transition-all duration-200 grayscale group-hover:grayscale-0"
                      />
                    </div>
                  )}
                  
                  {/* Category Name - Middle Section (Flex Grow) */}
                  <div className="flex-grow mb-3">
                    <h4 
                      className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 leading-tight"
                      title={cat.catName}
                    >
                      {cat.catName}
                    </h4>
                  </div>

                  {/* Action Buttons - Bottom Section (No Background Container) */}
                  <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(cat);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-all duration-150 ease-out hover:scale-110"
                      title="Edit"
                      aria-label="Edit category"
                    >
                      <Pencil size={13} strokeWidth={2} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(cat.catId);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-all duration-150 ease-out hover:scale-110"
                      title="Delete"
                      aria-label="Delete category"
                    >
                      <Trash2 size={13} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
